<script setup lang="ts">
/**
 * Findings workspace — the second tab of the matter's Narratives section
 * (see NarrativeTabs). Reuses the same shell as NarrativesPage (far-left icon
 * rail + section tab bar + main content) and lists the matter's findings from
 * the findings API (routers/findings_router.py, GET /Matter/{id}/Findings).
 * Findings are the free-text conclusions that narratives are assembled from
 * (docs_for_claude/narratives/narratives-backend.md). Read-only for now: no
 * create/edit affordance in the UI yet.
 */
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import MatterNavRail from '../matter/MatterNavRail.vue';
import NarrativeTabs from './NarrativeTabs.vue';
import { useMatterStore } from '../../stores/matterStore';
import { useFindingsStore } from '../../stores/findingsStore';
import { formatDate } from '../../utils/dateFormatting';

const { t } = useI18n();
const matterStore = useMatterStore();
const findingsStore = useFindingsStore();

const matterId = computed(() => matterStore.currentMatter?.RecordId ?? null);

// Load on mount and whenever the active matter changes. The route guard has
// already hydrated the matter store (requiresMatter), so currentMatter is set
// on a cold deep link too; `immediate` covers that first tick. Skip the fetch
// when the store already holds this matter's findings (e.g. tab round-trip).
watch(
    matterId,
    (id) => {
        if (!id) {
            findingsStore.reset();
            return;
        }
        if (findingsStore.loadedMatterId !== id) {
            void findingsStore.fetchFindings(id);
        }
    },
    { immediate: true },
);
</script>

<template>
    <div class="matter-page">
        <MatterNavRail />

        <div class="narrative-workspace">
            <NarrativeTabs />

            <div class="narrative-workspace-body">
                <div class="main-content">
                    <div class="chat-section">
                        <!-- Loading -->
                        <div
                            v-if="findingsStore.loading && findingsStore.findings.length === 0"
                            class="findings-status"
                        >
                            <div class="loading-dots">
                                <span></span><span></span><span></span>
                            </div>
                            <span>{{ t('narratives.findings.loading') }}</span>
                        </div>

                        <!-- Error -->
                        <div
                            v-else-if="findingsStore.error"
                            class="findings-status findings-status-error"
                        >
                            <span class="material-icons-outlined">error_outline</span>
                            <span>{{ t('narratives.findings.error') }}</span>
                            <button
                                type="button"
                                class="findings-retry-btn"
                                @click="matterId && findingsStore.fetchFindings(matterId)"
                            >
                                {{ t('narratives.findings.retry') }}
                            </button>
                        </div>

                        <!-- Empty -->
                        <div
                            v-else-if="findingsStore.findings.length === 0"
                            class="narratives-empty"
                        >
                            <span class="material-icons-outlined narratives-empty-icon"
                                >lightbulb</span
                            >
                            <h2 class="narratives-empty-title">
                                {{ t('narratives.findings.hero.title') }}
                            </h2>
                            <p class="narratives-empty-message">
                                {{ t('narratives.findings.hero.message') }}
                            </p>
                        </div>

                        <!-- List -->
                        <div v-else class="findings-list">
                            <h2 class="findings-list-heading">
                                {{ t('narratives.findings.hero.title') }}
                            </h2>
                            <ul class="findings-items">
                                <li
                                    v-for="finding in findingsStore.findings"
                                    :key="finding.id"
                                    class="findings-item"
                                >
                                    <p class="findings-item-desc">{{ finding.description }}</p>
                                    <p class="findings-item-meta">
                                        {{ formatDate(finding.created_on) }}
                                    </p>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
